<template>
  <div class="multi-select-container">
    <label v-if="label" class="multi-select-label">{{ label }}</label>
    <p v-if="description" class="multi-select-description">{{ description }}</p>
    
    <div class="options-container">
      <div 
        v-for="option in options" 
        :key="option.id || option.value || option"
        class="option-chip"
        :class="{ selected: isSelected(option) }"
        @click="toggleOption(option)"
      >
        {{ getOptionLabel(option) }}
        <div class="option-checkbox">
          <div class="checkbox-inner" v-if="isSelected(option)">
            <v-icon size="small" color="white">mdi-check</v-icon>
          </div>
        </div>
      </div>
      
      <!-- Other option -->
      <div 
        v-if="allowOther"
        class="option-chip"
        :class="{ selected: isOtherSelected }"
        @click="toggleOther"
      >
        Other
        <div class="option-checkbox">
          <div class="checkbox-inner" v-if="isOtherSelected">
            <v-icon size="small" color="white">mdi-check</v-icon>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Other input field -->
    <div v-if="allowOther && isOtherSelected" class="other-input-container">
      <input
        type="text"
        class="other-input"
        :placeholder="otherPlaceholder"
        :value="otherValue"
        @input="updateOtherValue"
      />
    </div>
    
    <div v-if="error" class="error-message">{{ error }}</div>
  </div>
</template>

<script>
export default {
  name: 'MultiSelectWithOther',
  props: {
    label: {
      type: String,
      default: ''
    },
    description: {
      type: String,
      default: ''
    },
    options: {
      type: Array,
      required: true
    },
    modelValue: {
      type: Array,
      default: () => []
    },
    otherValue: {
      type: String,
      default: ''
    },
    allowOther: {
      type: Boolean,
      default: true
    },
    otherPlaceholder: {
      type: String,
      default: 'Specify other option...'
    },
    error: {
      type: String,
      default: ''
    },
    // Option configuration
    optionKey: {
      type: String,
      default: 'id' // Can be 'id', 'value', or null for simple arrays
    },
    optionLabel: {
      type: String,
      default: 'name' // Can be 'name', 'label', or null for simple arrays
    }
  },
  data() {
    return {
      otherSelected: false
    };
  },
  computed: {
    selectedValues() {
      return Array.isArray(this.modelValue) ? this.modelValue : [];
    },
    isOtherSelected() {
      // Show as selected if either we manually selected it OR there's content in otherValue
      return this.otherSelected || (this.otherValue && this.otherValue.trim() !== '');
    }
  },
  watch: {
    // Watch for changes in otherValue prop to sync our local state
    otherValue: {
      handler(newValue) {
        if (!newValue || newValue.trim() === '') {
          this.otherSelected = false;
        } else {
          this.otherSelected = true;
        }
      },
      immediate: true
    }
  },
  methods: {
    getOptionKey(option) {
      if (typeof option === 'string' || typeof option === 'number') {
        return option;
      }
      return this.optionKey ? option[this.optionKey] : option.id || option.value;
    },
    getOptionLabel(option) {
      if (typeof option === 'string' || typeof option === 'number') {
        return option;
      }
      return this.optionLabel ? option[this.optionLabel] : option.name || option.label || option;
    },
    isSelected(option) {
      const key = this.getOptionKey(option);
      // Handle both array of primitives and array of objects
      return this.selectedValues.some(value => {
        if (typeof value === 'object' && value !== null) {
          return value.id === key || value.value === key;
        }
        return value === key;
      });
    },
    toggleOption(option) {
      const key = this.getOptionKey(option);
      let newValue = [...this.selectedValues];
      
      if (this.isSelected(option)) {
        newValue = newValue.filter(item => {
          if (typeof item === 'object' && item !== null) {
            return item.id !== key && item.value !== key;
          }
          return item !== key;
        });
      } else {
        newValue.push(key);
      }
      
      this.$emit('update:modelValue', newValue);
    },
    toggleOther() {
      if (this.isOtherSelected) {
        // Clear other value and hide input
        this.otherSelected = false;
        this.$emit('update:otherValue', '');
      } else {
        // Show input field
        this.otherSelected = true;
        // Focus on the input that will appear
        this.$nextTick(() => {
          const input = this.$el.querySelector('.other-input');
          if (input) input.focus();
        });
      }
    },
    updateOtherValue(event) {
      const value = event.target.value;
      this.$emit('update:otherValue', value);
      
      // If user clears the input, also clear our selection state
      if (!value || value.trim() === '') {
        this.otherSelected = false;
      } else {
        this.otherSelected = true;
      }
    }
  }
};
</script>

<style scoped>
.multi-select-container {
  margin-bottom: 20px;
}

.multi-select-label {
  display: block;
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 15px;
  color: #333;
}

.multi-select-description {
  font-size: 14px;
  color: #666;
  margin-bottom: 15px;
}

.options-container {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 15px;
}

.option-chip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background-color: #f0f0f0;
  border: 1px solid #e0e0e0;
  border-radius: 25px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 120px;
}

.option-chip:hover {
  background-color: #e0e0e0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.option-chip.selected {
  background-color: #e94057;
  color: white;
  border-color: #e94057;
}

.option-checkbox {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #ddd;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 10px;
  flex-shrink: 0;
}

.option-chip.selected .option-checkbox {
  border-color: white;
  background-color: rgba(255, 255, 255, 0.2);
}

.checkbox-inner {
  display: flex;
  align-items: center;
  justify-content: center;
}

.other-input-container {
  margin-top: 15px;
}

.other-input {
  width: 100%;
  padding: 12px 15px;
  font-size: 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #fff;
  transition: border-color 0.2s ease;
}

.other-input:focus {
  outline: none;
  border-color: #e94057;
  box-shadow: 0 0 0 2px rgba(233, 64, 87, 0.1);
}

.error-message {
  color: #e94057;
  font-size: 14px;
  margin-top: 5px;
}

@media (max-width: 768px) {
  .options-container {
    gap: 10px;
  }
  
  .option-chip {
    padding: 10px 15px;
    font-size: 13px;
    min-width: 100px;
  }
}
</style> 